import api from "../../services/api/project";
import Vue from 'vue'
import { ASSET_COPY_FAILED, ASSET_COPY_PARTIAL, DUPLICATING_STATUSES } from '../../constants/video-projects';

const ITEM_TYPE = 'project';
const ITEM = 'Projects'
const RECENT_PROJECTS_LIMIT = 4
const DUPLICATION_POLL_MS = 4000
const DUPLICATION_POLL_MAX_MS = 30000
// Without a copy worker a project stays "pending" forever; stop rather than refetch all day.
const DUPLICATION_POLL_MAX_TRIES = 40

let duplicationPoll = null
let duplicationDelay = DUPLICATION_POLL_MS
let duplicationTries = 0
let duplicationGaveUp = false
// Read at fire time, not captured: a search while the timer is armed would otherwise be reverted.
let duplicationFilter = null
const duplicationPending = new Map()

// The editor hands thumbnails over as blob: URLs it owns, and a strip rebuild can revoke one at
// any moment. Read them into Files up front so a later revoke cannot break the upload; a picture
// that is already gone is skipped rather than failing the whole save (POR-14426).
async function thumbnailFiles(thumbnails = []) {
  const files = await Promise.all(thumbnails.map(async ({ Image, Scene }) => {
    try {
      return { thumbnail: await api.base64ToFile(Image), sceneNumber: Scene }
    } catch (error) {
      console.warn('[saveProject] thumbnail skipped for scene', Scene, error)

      return null
    }
  }))

  return files.filter(Boolean)
}

function hasDuplicating(projects) {
  return projects.some(({ asset_copy_status: status }) => DUPLICATING_STATUSES.includes(status))
}

// The card is still "Duplicating..." when the request returns, so this waits for the job instead.
function announceFinishedDuplicates(projects) {
  if (!duplicationPending.size) {
    return
  }

  projects.forEach(({ id, name, asset_copy_status: status }) => {
    if (!duplicationPending.has(id) || DUPLICATING_STATUSES.includes(status)) {
      return
    }

    duplicationPending.delete(id)

    if (status === ASSET_COPY_FAILED) {
      toastr.error(`${name} could not be duplicated.`)
    } else if (status === ASSET_COPY_PARTIAL) {
      toastr.warning(`${name} is ready, but some of its files are still shared with the original.`)
    } else {
      toastr.success(`${name} duplicated successfully.`)
    }
  })
}

async function updateDataAndCommit({ commit, itemType, apiFunction, payload }) {
  if (payload) {
    await apiFunction(payload);
  } else {
    await apiFunction({ item_type: itemType });
  }
  
  const response = await api.fetchFolders(itemType);
  commit('SET_FOLDERS', response.data.folders);
}

const module = {
  namespaced: true,

  state: {
    showCommentBox: false,
    showImageDimensions: false,
    showPins: true,
    commentText: "",
    commentAttachments: [],
    commentStatus: null,
    commentEditing: null,
    commentReplying: null,

    folders:[],
    projects: [],
    projectsDrafts: [],
    projectsNew: [],
    projectsRecent: [],
    tags: [],
    galleryMode: false,
    openFolder: {
      id: '',
      name: '',
      mode: false,
    },
    selectedFolderData: {
      folderId: '',
      project: ''
    },
    navProjectsTitle: ITEM,
    modalFolders:[],
  },

  getters: {
    folders: state => state.folders,
    projects: state => state.projects,

    projectsDrafts(state) {
      return state.projectsDrafts;
    },
    projectsNew(state) {
      return state.projectsNew;
    },
    projectsRecent(state) {
      return state.projectsRecent;
    },
    tags: state => _.map(state.tags, 'text'),

    showCommentBox: state => state.showCommentBox,
    showImageDimensions: state => state.showImageDimensions,
    commentText: state => state.commentText,
    commentAttachments: state => state.commentAttachments,
    commentStatus: state => state.commentStatus,
    commentEditing: state => state.commentEditing,
    commentReplying: state => state.commentReplying,
    galleryMode: state => state.galleryMode,
    showPins: state => state.showPins,
    openFolderMode: state => state.openFolder.mode,
    selectedFolderData: state => state.selectedFolderData,
    navProjectsTitle: state => state.navProjectsTitle,
    modalFolders: state => state.modalFolders,
  },

  mutations: {
    SET_FOLDERS(state, folders) {
      state.folders = folders;
    },
    SET_MODAL_FOLDERS(state, folders) {
      state.modalFolders = folders;
    },
    SET_OPEN_FOLDER(state, folder) {
      state.openFolder.id = folder.id;
      state.openFolder.name = folder.name;
      state.openFolder.mode = folder.mode;
    },
    SET_SELECTED_PROJECT(state, project) {
      state.selectedFolderData.project = project
    },
    SET_SELECTED_FOLDER(state, folderId) {
      state.selectedFolderData.folderId = folderId
    },
    
    SET_PROJECTS(state, projects) {
      state.projects = projects;
    },
    UPDATE_PROJECT (state, payload) {
      const index = _.findIndex(state.projects, ['id', payload.id])
      if (index !== -1) {
        Vue.set(state.projects, index, payload)
      }
    },
    SET_PROJECTS_DRAFTS(state, projects) {
      state.projectsDrafts = projects;
    },
    SET_PROJECTS_NEW(state, projectsNew) {
      state.projectsNew = projectsNew;
    },
    SET_PROJECTS_RECENT(state, projects) {
      state.projectsRecent = projects;
    },
    SET_PROJECTS_TITLE(state, folderName) {
      state.navProjectsTitle = `${folderName}`
    },

    DELETE_PROJECT (state, payload) {
      const index = _.findIndex(state.projects, ['id', payload.id])
      if (index !== -1) {
        state.projects.splice(index, 1)
      }
    },

    DELETE_PROJECT_DRAFT (state, payload) {
      const index = _.findIndex(state.projectsDrafts, ['id', payload.id])
      if (index !== -1) {
        state.projectsDrafts.splice(index, 1)
      }
    },

    UPDATE_PROJECT_TAGS (state, { id, tag }) {
      updateTags(state.projects, id, tag, 'attach')
    },

    REMOVE_PROJECT_TAG (state, { id, tag }) {
      updateTags(state.projects, id, tag, 'detach')
    },

    UPDATE_DRAFT_PROJECT_TAGS (state, { id, tag }) {
      updateTags(state.projectsDrafts, id, tag, 'attach')
    },

    REMOVE_DRAFT_PROJECT_TAG (state, { id, tag }) {
      updateTags(state.projectsDrafts, id, tag, 'detach')
    },

    FETCH_TAGS (state, tags) {
      state.tags = tags
    },

    SET_SHOW_PINS (state, status) {
      state.showPins = status
    },
    SET_COMMENT_BOX(state, status) {
      state.showCommentBox = status;
    },
    SET_SHOW_IMAGE_DIMENSIONS (state, status) {
      state.showImageDimensions = status
    },
    SET_COMMENT_TEXT(state, text) {
      state.commentText = text;
    },
    SET_COMMENT_ATTACHMENTS(state, attachments) {
      state.commentAttachments = attachments;
    },
    SET_COMMENT_STATUS (state, status) {
      state.commentStatus = status
    },
    SET_COMMENT_EDITING(state, id) {
      state.commentEditing = id;
    },
    SET_COMMENT_REPLYING (state, id) {
      state.commentReplying = id
    },
    SET_GALLERY_MODE (state, payload) {
      state.galleryMode = payload
    },
  },

  actions: {
    //FOLDERS
    async fetchFolder({ commit }, payload) {
      const response = await api.fetchFolders(payload);
      commit('SET_FOLDERS', response.data.folders);
      commit('SET_MODAL_FOLDERS', response.data.folders);
    },
    async createFolder({ commit }) {
      await updateDataAndCommit(
        {
          commit,
          itemType: ITEM_TYPE,
          apiFunction: api.createFolder,
        }
      );
    },
    async editFolder({ commit }, payload) {
      await updateDataAndCommit(
        {
          commit,
          itemType: ITEM_TYPE,
          apiFunction: api.updateFolder,
          payload
        }
      );
    },
    async deleteFolder({ commit }, payload) {
      await updateDataAndCommit(
        {
          commit,
          itemType: ITEM_TYPE,
          apiFunction: api.deleteFolder,
          payload
        }
      );
    },
    async deleteFolderItem({ commit, state }, payload) {
      await api.deleteFolderItem(payload);
      const response = await api.folderContent({ id: state.openFolder.id });
      commit('SET_FOLDERS', response.data.folders);
      commit("SET_PROJECTS", response.data.projects);
    },
    async openFolder({ commit }, payload) {
      const response = await api.folderContent({ id: payload.id });
      commit('SET_PROJECTS_TITLE', `${ITEM} - ${payload.name}`);
      commit('SET_OPEN_FOLDER', { id: payload.id, name:payload.name, mode: true });
      commit('SET_FOLDERS', response.data.folders);
      commit("SET_PROJECTS", response.data.projects);
    },
    async goBack({ commit }, payload) {
      const foldersData = await api.fetchFolders(ITEM_TYPE);
      const projectsData = await api.getProjects(payload);
      commit('SET_PROJECTS_TITLE', ITEM);
      commit('SET_OPEN_FOLDER', { id: '', name: '', mode: false });
      commit('SET_FOLDERS', foldersData.data.folders);
      commit("SET_PROJECTS", projectsData.data.projects);
    },
    async searchModalFolders({ commit }, payload) {
      const response = await api.fetchFolders(payload);
      commit('SET_MODAL_FOLDERS', response.data.folders);
    },
    async searchFolders({ commit }, payload) {
      const response = await api.fetchFolders(payload);
      commit('SET_FOLDERS', response.data.folders);
    },
    setSelectedProject({ commit }, payload) {
      commit('SET_SELECTED_PROJECT', payload);
    },
    setSelectedFolder({ commit }, payload) {
      commit('SET_SELECTED_FOLDER', payload);
    },
    async addToProjectFolder({ commit }, payload) {
      await api.addToProjectFolder(payload);
      const response = await api.getProjects(payload);
      commit("SET_PROJECTS", response.data.projects);
    },
    async moveFromProjectFolder({ commit, state }, payload) {
      await api.moveFromProjectFolder({ project: payload });
      const response = await api.folderContent({ id: state.openFolder.id });
      commit('SET_FOLDERS', response.data.folders);
      commit("SET_PROJECTS", response.data.projects);
    },
    async searchFolderContent({ commit, state }, payload) {
      const response = await api.folderContent({
        id: state.openFolder.id,
        search: payload?.search,
        tags: payload?.tags
      });
      commit('SET_FOLDERS', response.data.folders);
      commit("SET_PROJECTS", response.data.projects);
    },
    
    //PROJECTS
    async getProjects({ commit }, payload) {
      const response = await api.getProjects(payload);
      commit("SET_PROJECTS", response.data.projects);
    },
    async getProjectsDrafts({commit}, payload = {}) {
      payload.drafts = true
      const response = await api.getProjects(payload);
      commit("SET_PROJECTS_DRAFTS", response.data.projectsDrafts);
    },
    async getProjectsNew({commit}, payload = {}) {
      const response = await api.getProjectsNew(payload);
      commit("SET_PROJECTS_NEW", response.data.newProjects);
    },
    // Its own list rather than a slice of `projects`: that one drops drafts and anything
    // filed in a folder, which a just-edited project very often is. (POR-14439)
    async getProjectsRecent({commit}, payload = {}) {
      const response = await api.getProjects({ recent: payload.limit || RECENT_PROJECTS_LIMIT });
      commit("SET_PROJECTS_RECENT", response.data.projectsRecent);
    },
    async saveProject({ commit, dispatch }, { quiet = false, ...payload }) {
      // Before the rows request: the blob URLs must be read while they are still valid.
      const thumbnails = await thumbnailFiles(payload.thumbnails);
      const { data: { message, data: { project } } } = await api.saveProject(payload);
      if (thumbnails.length < (payload.thumbnails || []).length) {
        toastr.warning('Some scene thumbnails could not be read and were not saved.');
      }
      if (thumbnails.length) {
        try {
          project.thumbnails = await dispatch('updateThumbnails', { id: project.id, thumbnails });
        } catch (error) {
          // The rows are saved by now; failing here only leaves the project card's pictures stale,
          // so it must not surface as "Project saving failed".
          console.error('[saveProject] thumbnails', error);
          toastr.warning('The project was saved, but its scene thumbnails could not be updated.');
        }
      }
      if (payload.id) {
        commit('UPDATE_PROJECT', project);
      }
      if (!quiet) {
        toastr.success(message);
      }
      return project;
    },
    async duplicateProject({ dispatch, state }, { project, filter }) {
      // Without this the copy lands at the root, and the folder the user is looking at never shows it.
      const payload = state.openFolder.mode ? { folder_id: state.openFolder.id } : {};
      const { data } = await api.duplicateProject(project.id, payload);
      const copy = data.data.project;

      duplicationPending.set(copy.id, copy.name);
      duplicationDelay = DUPLICATION_POLL_MS;
      duplicationTries = 0;
      duplicationGaveUp = false;

      toastr.info(`Duplicating ${project.name}...`);
      await dispatch('refreshProjects', filter);

      return copy;
    },

    refreshProjects({ dispatch, state }, filter) {
      return dispatch(state.openFolder.mode ? 'searchFolderContent' : 'getProjects', filter || {});
    },

    // Polling until the job broadcasts on user.{id} the way ai-motion and video-text-removal do.
    watchDuplicating({ dispatch, state }, filter) {
      announceFinishedDuplicates(state.projects);
      duplicationFilter = filter;

      if (!hasDuplicating(state.projects)) {
        return dispatch('stopWatchingDuplicating');
      }

      if (duplicationTries >= DUPLICATION_POLL_MAX_TRIES) {
        duplicationGaveUp = true;
      }

      if (duplicationGaveUp) {
        return;
      }

      // Every SET_PROJECTS re-runs this, and re-arming each time would push the poll back forever.
      if (duplicationPoll) {
        return;
      }

      duplicationPoll = setTimeout(() => {
        duplicationPoll = null;
        duplicationTries += 1;
        duplicationDelay = Math.min(duplicationDelay * 2, DUPLICATION_POLL_MAX_MS);

        // Only a successful commit re-runs the watcher, so a failed poll has to re-arm itself.
        dispatch('refreshProjects', duplicationFilter)
          .catch(() => dispatch('watchDuplicating', duplicationFilter));
      }, duplicationDelay);
    },

    stopWatchingDuplicating() {
      clearTimeout(duplicationPoll);
      duplicationPoll = null;
      duplicationDelay = DUPLICATION_POLL_MS;
      duplicationTries = 0;
      duplicationGaveUp = false;
    },

    async copyProject({ commit }, id) {
      const { data } = await api.copyProject(id);
      const message = data.message;
      const project = data.data;
      commit('UPDATE_PROJECT', project);
      toastr.success(message);
      return data;
    },
    // `thumbnails`: either the editor's `{ Image, Scene }` entries or, from saveProject, Files already
    // read by thumbnailFiles() as `{ thumbnail, sceneNumber }`.
    async updateThumbnails ({ commit }, payload) {
      const thumbnails = await Promise.all(payload.thumbnails.map(async (item) => (
        item.thumbnail instanceof File
          ? item
          : { thumbnail: await api.base64ToFile(item.Image), sceneNumber: item.Scene }
      )))
      await api.clearThumbnails(payload.id)
      const chunks = _.chunk(thumbnails, 20)
      const res = await Promise.all(chunks.map(thumbnails => api.saveThumbnails({
        thumbnails,
        id: payload.id,
      })))
      return _.flatMap(res, 'data')
    },
    loadProject({ commit }, id) {
     return api.loadProject(id);
    },

    async deleteProject ({ commit }, payload) {
      await api.deleteProject(payload.id)
      commit('DELETE_PROJECT', payload)
    },

    async deleteProjectDraft ({ commit }, payload) {
      await api.deleteProject(payload.id)
      commit('DELETE_PROJECT_DRAFT', payload)
    },

    async attachTagToProject ({ commit }, { id, tag }) {
      const { data } = await api.attachTagToProject(id, tag)
      commit('UPDATE_PROJECT_TAGS', { id, tag: data })
    },

    async detachTagFromProject ({ commit }, { id, tag }) {
      const { data } = await api.detachTagFromProject(id, tag)
      commit('REMOVE_PROJECT_TAG', { id, tag: data })
    },

    async attachTagToDraftProject ({ commit }, { id, tag }) {
      const { data } = await api.attachTagToProject(id, tag)
      commit('UPDATE_DRAFT_PROJECT_TAGS', { id, tag: data })
    },

    async detachTagFromDraftProject ({ commit }, { id, tag }) {
      const { data } = await api.detachTagFromProject(id, tag)
      commit('REMOVE_DRAFT_PROJECT_TAG', { id, tag: data })
    },

    async fetchTags ({ commit }) {
      const { data } = await api.getAllTags()
      commit('FETCH_TAGS', data)
    },

    updateCommentBox ({ commit }, val) {
      commit('SET_COMMENT_BOX', val)
    },
    updateCommentText ({ commit }, text) {
      commit('SET_COMMENT_TEXT', text)
    },
    updateCommentAttachments ({ commit }, attachments) {
      commit('SET_COMMENT_ATTACHMENTS', attachments)
    },
    updateCommentStatus ({ commit }, status) {
      commit('SET_COMMENT_STATUS', status)
    },
    updateCommentEditing ({ commit }, val) {
      commit('SET_COMMENT_EDITING', val)
    },
    updateCommentReplying ({ commit }, val) {
      commit('SET_COMMENT_REPLYING', val)
    },
  },
};

function updateTags(projectList, id, tag, operation) {
  const project = projectList.find(project => project.id === id)
  if (!project) {
    return
  }

  if (operation === 'attach') {
    project.tags.push(tag)
  } else if (operation === 'detach') {
    const index = _.findIndex(project.tags, ['id', tag.id])
    if (index !== -1) {
      project.tags.splice(index, 1)
    }
  }
}

export default module;
